import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/i18n";
import { Button } from "@/components/ui/button";
import { LogOut, ClipboardCheck, LayoutDashboard, ScrollText, Users, ListPlus, Bell, AlertTriangle, UserCog } from "lucide-react";
import { MyAccountDialog } from "@/components/MyAccountDialog";

export default function Layout({ children, title, subtitle }) {
  const { user, logout } = useAuth();
  const { property } = useProperty();
  const nav = useNavigate();
  const loc = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  useEffect(() => {
    if (user?.role === "owner") {
      api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    }
  }, [user, loc.pathname]);

  const unread = notifs.filter((n) => !n.read).length;

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch { /* ignore */ }
  };

  const links = [];
  if (user?.role === "supervisor") {
    links.push({ to: "/inspect", label: "New Checklist", icon: ClipboardCheck, testid: "nav-link-checklist" });
  }
  if (user?.role === "owner") {
    links.push({ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-link-dashboard" });
    links.push({ to: "/points", label: "Checklist Points", icon: ListPlus, testid: "nav-link-points" });
  }
  links.push({ to: "/inspections", label: "Cleaning Log", icon: ScrollText, testid: "nav-link-reports" });
  if (user?.role === "owner") {
    links.push({ to: "/admin", label: "Users", icon: Users, testid: "nav-link-users" });
  }

  const brandName = property?.name || "Tamanna Hotels Group";
  const brandSub = property?.code || "Grand · Premier · Bizz · Executive · Imperial";

  return (
    <div className="min-h-screen bg-[var(--linen)] paper-grain">
      <header className="glass-header sticky top-0 z-30 no-print">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-[var(--navy)] text-[var(--gold)] font-display font-bold text-2xl flex items-center justify-center border border-[var(--gold)]/40">T</div>
            <div className="hidden sm:block">
              <div className="hotel-overline text-[var(--gold)]">{brandSub}</div>
              <div className="font-display text-xl leading-tight text-[var(--navy)]">{brandName}</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => {
              const active = loc.pathname === l.to;
              const Icon = l.icon;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={l.testid}
                  className={`px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                    active ? "bg-[var(--navy)] text-white" : "text-[var(--ink-2)] hover:bg-[var(--gold-light)] hover:text-[var(--navy)]"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {l.label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-3">
            {user?.role === "owner" && (
              <div className="relative">
                <button
                  type="button"
                  data-testid="notification-bell-button"
                  onClick={() => setBellOpen((o) => !o)}
                  className="relative w-10 h-10 rounded-md border border-[var(--border-warm)] bg-white flex items-center justify-center hover:bg-[var(--gold-light)] transition-colors"
                >
                  <Bell className="w-4 h-4 text-[var(--navy)]" />
                  {unread > 0 && (
                    <span data-testid="notification-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--danger)] text-white text-[10px] font-bold flex items-center justify-center">
                      {unread}
                    </span>
                  )}
                </button>
                {bellOpen && (
                  <div data-testid="notification-panel" className="absolute right-0 mt-2 w-96 max-h-[480px] overflow-y-auto bg-white border border-[var(--border-warm)] rounded-lg shadow-xl z-50">
                    <div className="flex items-center justify-between p-4 border-b border-[var(--border-subtle)] bg-[var(--surface-elev)]">
                      <div className="hotel-overline">Failed-item alerts</div>
                      {unread > 0 && (
                        <button type="button" data-testid="mark-all-read-button" onClick={markAllRead} className="text-xs text-[var(--gold)] hover:underline font-semibold">
                          Mark all read
                        </button>
                      )}
                    </div>
                    {notifs.length === 0 ? (
                      <div className="p-6 text-center text-sm text-[var(--muted)]">No alerts. All checklists fully passed.</div>
                    ) : (
                      <ul className="divide-y divide-[var(--border-subtle)]">
                        {notifs.map((n) => (
                          <li key={n.id} data-testid={`notification-item-${n.id}`} className={n.read ? "opacity-60" : ""}>
                            <Link
                              to={`/inspections/${n.inspection_id}`}
                              onClick={() => setBellOpen(false)}
                              className="block p-4 hover:bg-[var(--surface-elev)] transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-[var(--danger)] shrink-0" />
                                <span className="font-semibold text-sm text-[var(--navy)]">
                                  Room {n.room_number} · {n.property_name?.replace(" Hotel", "")}
                                </span>
                                {!n.read && <span className="w-2 h-2 rounded-full bg-[var(--danger)] ml-auto shrink-0" />}
                              </div>
                              <div className="text-xs text-[var(--ink-2)] mt-1">
                                {n.failed_items?.length} item(s) marked No by {n.supervisor_name}
                              </div>
                              <div className="text-[11px] text-[var(--muted)] mt-1 line-clamp-2">
                                {n.failed_items?.slice(0, 2).map((f) => f.text).join(" · ")}{n.failed_items?.length > 2 ? " …" : ""}
                              </div>
                              <div className="text-[10px] font-mono-hotel text-[var(--muted)] mt-1">{fmtDateTime(n.created_at)}</div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
            {user && (
              <div className="hidden sm:flex flex-col items-end leading-tight">
                <span data-testid="current-role-badge" className="hotel-overline text-[var(--gold)]">{user.role}</span>
                <span className="text-sm font-semibold text-[var(--navy)]">{user.name}</span>
              </div>
            )}
            {user?.role === "owner" && (
              <button
                type="button"
                data-testid="my-account-button"
                title="My Account"
                onClick={() => setAccountOpen(true)}
                className="w-10 h-10 rounded-md border border-[var(--border-warm)] bg-white flex items-center justify-center hover:bg-[var(--gold-light)] transition-colors"
              >
                <UserCog className="w-4 h-4 text-[var(--navy)]" />
              </button>
            )}
            <Button
              variant="outline"
              size="sm"
              data-testid="logout-button"
              onClick={() => { logout(); nav("/"); }}
              className="border-[var(--border-warm)] text-[var(--ink)] hover:bg-[var(--gold-light)]"
            >
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
        <div className="gold-underline" />
      </header>
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {(title || subtitle) && (
          <div className="mb-8 fade-up">
            {subtitle && <div className="hotel-overline mb-2">{subtitle}</div>}
            {title && <h1 className="font-display text-4xl sm:text-5xl text-[var(--navy)] leading-tight">{title}</h1>}
          </div>
        )}
        {children}
      </main>
      <MyAccountDialog open={accountOpen} onOpenChange={setAccountOpen} />
      <footer className="max-w-[1400px] mx-auto px-6 py-8 no-print">
        <div className="gold-underline mb-4" />
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-[var(--muted)]">
          <div className="font-mono-hotel">SOP-HK / v2.2 · {brandName}</div>
          <div>© {new Date().getFullYear()} Tamanna Hotels Group · Housekeeping Quality System</div>
        </div>
      </footer>
    </div>
  );
}
